import {
  Alert,
  AlertDescription,
  Button,
  cn,
  FormInput,
  FormRoot,
  FormSelect,
  Label,
  RHF,
  Switch,
  zodResolver,
} from "@devhop/ui";
import {
  GlobeIcon,
  InfoIcon,
  LayoutGridIcon,
  SaveIcon,
  StoreIcon,
} from "lucide-react";
import { z } from "zod";
import { CascadingCategorySelect } from "@/modules/catalog/presentation/ui/CascadingCategorySelect";
import { useCategoriesQuery } from "@/modules/categories/presentation/api/queries";
import type {
  FeeRuleCreateInput,
  FeeRuleUpdateInput,
} from "@/modules/fees/domain/contracts";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { feesApi } from "../api/client";

const SCOPE_OPTIONS = [
  { value: "global" as const, label: "ທົ່ວແຜງ", icon: GlobeIcon },
  { value: "shop" as const, label: "ຮ້ານຄ້າ", icon: StoreIcon },
  { value: "category" as const, label: "ຫມວດຫມູ່", icon: LayoutGridIcon },
];

const FEE_TYPE_OPTIONS = [
  { value: "marketplace_commission", label: "ຄ່າຄອມມິດຊັນ Marketplace" },
  { value: "mechanic_commission", label: "ຄ່າທຳນຽມງານຊ່ງ" },
];

const FEE_MODE_OPTIONS = [
  { value: "percent", label: "ເປີເຊັນ (%)" },
  { value: "flat", label: "ຈຳນວນຄົງທີ່" },
];

const CURRENCY_OPTIONS = [
  { value: "LAK", label: "LAK (K)" },
  { value: "THB", label: "THB" },
  { value: "USD", label: "USD" },
];

const FeeRuleFormSchema = z
  .object({
    scope: z.enum(["global", "shop", "category"]),
    scopeId: z.union([z.string().uuid(), z.null(), z.literal("")]),
    feeType: z.enum(["marketplace_commission", "mechanic_commission"]),
    feeMode: z.enum(["percent", "flat"]),
    value: z.coerce.number().min(0, "ຕ້ອງໃສ່ຄ່າບໍ່ຕ່ຳກວ່າ 0"),
    currency: z.enum(["LAK", "THB", "USD"]),
    isActive: z.boolean(),
  })
  .refine(
    (data) => {
      if (data.scope !== "global" && !data.scopeId) return false;
      if (data.scope === "global" && data.scopeId) return false;
      return true;
    },
    { message: "ເມື່ອບໍ່ແມ່ນທົ່ວແຜງ ຕ້ອງເລືອກຮ້ານ ຫຼື ຫມວດຫມູ່", path: ["scopeId"] },
  )
  .refine(
    (data) => {
      if (data.feeMode === "flat") return !!data.currency;
      return true;
    },
    { message: "ແບບຈຳນວນຄົງທີ່ຕ້ອງເລືອກສະກຸນເງິນ", path: ["currency"] },
  );

export type FeeRuleFormValues = {
  scope: "global" | "shop" | "category";
  scopeId: string | null | "";
  feeType: "marketplace_commission" | "mechanic_commission";
  feeMode: "percent" | "flat";
  value: number;
  currency: "LAK" | "THB" | "USD";
  isActive: boolean;
};

function shopsQueryFn(args: { search: string; pageParam: number }) {
  const limit = 20;
  const skip = (args.pageParam - 1) * limit;
  return feesApi
    .lookupShops({ q: args.search || undefined, limit, skip })
    .then((r) => ({
      items: r.items,
      nextPage: skip + r.items.length < r.total ? args.pageParam + 1 : null,
    }));
}

export function FeeRuleForm({
  initialValues,
  onSubmit,
  onCancel,
  submitting,
  mode = "create",
}: {
  initialValues?: Partial<FeeRuleFormValues>;
  onSubmit: (values: FeeRuleCreateInput | FeeRuleUpdateInput) => void;
  onCancel?: () => void;
  submitting?: boolean;
  mode?: "create" | "edit";
}) {
  const methods = RHF.useForm<FeeRuleFormValues>({
    resolver: zodResolver(FeeRuleFormSchema) as RHF.Resolver<FeeRuleFormValues>,
    defaultValues: {
      scope: "global",
      scopeId: null,
      feeType: "marketplace_commission",
      feeMode: "percent",
      value: 0,
      currency: "LAK",
      isActive: true,
      ...initialValues,
    },
  });

  const scope = methods.watch("scope");
  const scopeId = methods.watch("scopeId");
  const categoriesQuery = useCategoriesQuery({ limit: 1000, offset: 0 });
  const categoryItems = (categoriesQuery.data?.data ?? []).map((c) => ({
    id: c.id,
    name: c.name,
    parentId: c.parentId,
  }));

  const scopeIdResolved = (v: string | null | "" | undefined) =>
    v === "" || v == null ? null : v;

  const handleSubmit = (vals: FeeRuleFormValues) => {
    if (mode === "edit") {
      onSubmit({
        scope: vals.scope,
        scopeId: scopeIdResolved(vals.scopeId as string | null),
        feeMode: vals.feeMode,
        value: vals.value,
        currency: vals.currency,
        isActive: vals.isActive,
      });
    } else {
      onSubmit({
        scope: vals.scope,
        scopeId:
          vals.scope === "global"
            ? null
            : scopeIdResolved(vals.scopeId as string | null),
        feeType: vals.feeType,
        feeMode: vals.feeMode,
        value: vals.value,
        currency: vals.feeMode === "flat" ? vals.currency : "LAK",
        isActive: vals.isActive,
      });
    }
  };

  return (
    <FormRoot<FeeRuleFormValues>
      methods={methods}
      onSubmit={handleSubmit}
      className="space-y-6"
    >
      {/* RULE SCOPE */}
      <fieldset className="space-y-3">
        <legend
          id="rule-scope-label"
          className="mb-3 block font-medium text-muted-foreground text-sm uppercase tracking-wide"
        >
          ຂອບເຂດກົດ
        </legend>
        <div className="flex flex-wrap gap-3">
          {SCOPE_OPTIONS.map(({ value, label, icon: Icon }) => (
            <RHF.Controller
              key={value}
              control={methods.control}
              name="scope"
              render={({ field }) => (
                <button
                  type="button"
                  onClick={() => {
                    field.onChange(value);
                    methods.setValue("scopeId", null);
                  }}
                  className={cn(
                    "flex flex-1 flex-col items-center gap-2 rounded-xl border-2 px-4 py-3 font-medium text-sm transition-colors",
                    field.value === value
                      ? "border-[#F26522] bg-[#F26522]/10 text-[#F26522]"
                      : "border-border bg-card hover:bg-muted/50",
                  )}
                >
                  <Icon className="size-5" />
                  {label}
                </button>
              )}
            />
          ))}
        </div>
      </fieldset>

      {/* SELECT SHOP / CATEGORY */}
      {scope === "shop" && (
        <FormInfiniteCombobox<{ id: string; name: string }>
          name="scopeId"
          label="ເລືອກຮ້ານຄ້າ"
          queryKey={["fees", "lookup", "shops"]}
          queryFn={shopsQueryFn}
          preloadQueryFn={(id) => feesApi.getShopById(id)}
          getLabel={(item) => item.name}
          getValue={(item) => item.id}
          placeholder="ຄົ້ນຫາ ຫຼື ເລືອກຮ້ານ..."
          clearable
        />
      )}
      {scope === "category" && (
        <div className="space-y-1.5">
          <Label>
            ເລືອກຫມວດຫມູ່ <span className="text-destructive">*</span>
          </Label>
          <CascadingCategorySelect
            categories={categoryItems}
            value={typeof scopeId === "string" ? scopeId : ""}
            onChange={(val) =>
              methods.setValue("scopeId", val || null, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            isLoading={categoriesQuery.isLoading}
            requireLeaf={false}
            clearable
          />
          {methods.formState.errors.scopeId ? (
            <p className="text-destructive text-xs">
              {methods.formState.errors.scopeId.message}
            </p>
          ) : null}
        </div>
      )}

      {/* FEE DETAILS - two columns */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <FormSelect
            name="feeType"
            label="ປະເພດຄ່າທຳນຽມ"
            options={FEE_TYPE_OPTIONS}
          />
        </div>
        <div className="space-y-2">
          <FormSelect
            name="feeMode"
            label="ແບບຄິດໄລ່"
            options={FEE_MODE_OPTIONS}
          />
        </div>
        <div className="space-y-2">
          <FormInput
            name="value"
            label="ຄ່າ"
            type="number"
            step="0.01"
            min={0}
            requiredMark
            placeholder="0.00"
          />
        </div>
        <div className="space-y-2">
          <FormSelect
            name="currency"
            label="ສະກຸນເງິນ"
            options={CURRENCY_OPTIONS}
          />
        </div>
      </div>

      {/* Rule Status */}
      <fieldset
        id="rule-status-fieldset"
        className="flex items-center justify-between space-y-2 rounded-xl bg-muted px-4 py-2"
      >
        <div>
          <legend id="rule-status-label" className="block font-medium text-sm">
            ສະຖານະກົດ
          </legend>
          <p className="text-muted-foreground text-xs">ເປີດ ຫຼື ປິດກົດນີ້ໃຊ້ງານໄດ້ທັນທີ</p>
        </div>
        <RHF.Controller
          control={methods.control}
          name="isActive"
          render={({ field }) => (
            <Switch
              id="fee-rule-is-active"
              checked={field.value}
              onCheckedChange={field.onChange}
              className="data-[state=checked]:bg-[#F26522]"
              aria-labelledby="rule-status-fieldset"
            />
          )}
        />
      </fieldset>

      {/* Info box */}
      <Alert className="border-secondary/30 bg-secondary/5">
        <InfoIcon className="size-4 text-secondary" />
        <AlertDescription className="inline">
          ລຳດັບການນຳໃຊ້: <span className="font-semibold text-secondary">ຮ້ານ</span>{" "}
          &gt; <span className="font-semibold text-secondary">ຫມວດຫມູ່</span> &gt;{" "}
          <span className="font-semibold text-secondary">ທົ່ວແຜງ</span>.
          ກົດທີ່ເຈາະຈົງກວ່າຈະຖືກນຳໃຊ້ກ່ອນໃນການຄິດໄລ່.
        </AlertDescription>
      </Alert>

      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
        )}
        <Button type="submit" isLoading={submitting}>
          <SaveIcon className="size-4" />
          ບັນທຶກກົດຄ່າທຳນຽມ
        </Button>
      </div>
    </FormRoot>
  );
}
